import { api } from "./client";

export interface CouponConfig {
  id: string;
  code: string;
  discount_title: string;
  duration_text: string;
  expiry_minutes: number;
  applicable_plan: string;
  conditions: string[];
  is_active: boolean;
  updated_at?: string;
}

export interface ClaimedCoupon {
  id: string;
  user_id: string;
  user_email: string;
  coupon_code: string;
  discount_title: string;
  duration_text: string;
  applicable_plan: string;
  conditions: string[];
  status: "active" | "redeemed" | "expired";
  created_at: string;
  expires_at: string;
  redeemed_at?: string | null;
}

export interface MyCouponResponse {
  has_coupon?: boolean;
  has_active_coupon: boolean;
  coupon: ClaimedCoupon | null;
}

export interface AdminCouponResponse {
  config: CouponConfig;
  stats: {
    total_claims: number;
    active_claims: number;
    redeemed_claims: number;
  };
}

export async function getActiveCouponTemplate(): Promise<CouponConfig> {
  const res = await api.get<CouponConfig>("/coupon/active");
  return res.data;
}

export async function getMyActiveCoupon(): Promise<MyCouponResponse> {
  const res = await api.get<MyCouponResponse>("/coupon/my-coupon");
  return res.data;
}

export async function generateUserCoupon(): Promise<{
  status: "created" | "existing";
  message: string;
  coupon: ClaimedCoupon;
}> {
  const res = await api.post("/coupon/generate");
  return res.data;
}

export async function redeemUserCoupon(code: string): Promise<{
  status: string;
  message: string;
  coupon: ClaimedCoupon;
}> {
  const res = await api.post("/coupon/redeem", { code });
  return res.data;
}

export async function getAdminCouponConfig(): Promise<AdminCouponResponse> {
  try {
    const res = await api.get<AdminCouponResponse>("/coupon/admin/campaign");
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const fallback = await api.get<AdminCouponResponse>("/coupon/admin/config");
      return fallback.data;
    }
    throw err;
  }
}

export async function updateAdminCouponConfig(
  payload: Partial<CouponConfig>
): Promise<{ status: string; message: string; config: CouponConfig }> {
  try {
    const res = await api.put("/coupon/admin/campaign", payload);
    return res.data;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const fallback = await api.put("/coupon/admin/config", payload);
      return fallback.data;
    }
    throw err;
  }
}

export async function resetMyCoupon(): Promise<{ status: string; message: string }> {
  const res = await api.post("/coupon/reset");
  return res.data;
}

export async function clearAllAdminClaims(): Promise<{
  status: string;
  message: string;
  deleted: number;
}> {
  const res = await api.post("/coupon/admin/claims/clear");
  return res.data;
}

